import PortfolioComponent, { COMPONENT_TYPES } from "./portfolioComponent";
import PortfolioPosition from "./portfolioPosition";
import Category from "./category";
import { instanceToPlain, plainToInstance, Transform, TransformationType, TransformFnParams, Type } from "class-transformer";
import Position from "./portfolioPosition";
import RebalanceItem from "./rebalanceItem";

function transformFn(params: TransformFnParams) {
  const value = params.value as PortfolioComponent[];
  return value.map(component => {
    if (params.type === TransformationType.PLAIN_TO_CLASS) {
      if (component.componentType === COMPONENT_TYPES.COMPOSITE) {
        return plainToInstance(PortfolioComposite, component);
      } else if (component.componentType === COMPONENT_TYPES.POSITION) {
        return plainToInstance(PortfolioPosition, component);
      }
    } else if (params.type === TransformationType.CLASS_TO_PLAIN) {
      return Object(instanceToPlain(component));
    }
    return component;
  });
}

export default class PortfolioComposite extends PortfolioComponent {
  @Type(() => Category)
  category: Category;
  /** List of components of this portfolio. */
  @Transform(transformFn)
  portfolioComponents: PortfolioComponent[];
  /**
   * @param id GUID of this portfolio.
   * @param portfolioId ID of the portfolio to which this portfolio component belongs to.
   * @param category 
   * @param portfolioComponents List of components of this portfolio.
   * @param weight Relative weight to be given to this portfolio during rebalancing.
   * @param parentCompositeId ID of parent portfolio composite. If undefined, this component is the root composite.
   */
  constructor(
    public id: string | undefined,
    public portfolioId: string,
    category: Category,
    portfolioComponents: PortfolioComponent[],
    public weight: number,
    public parentCompositeId?: string
  ) {
    super();
    this.componentType = COMPONENT_TYPES.COMPOSITE;
    this.category = category;
    this.portfolioComponents = portfolioComponents;
  }

  notional(): number {
    return (!this.portfolioComponents || this.portfolioComponents.length === 0) ? 0 : this.portfolioComponents.reduce((accumulator, current) => accumulator + current.notional(), 0);
  }

  /** Returns the weighted average absolute deviation of the compenents, weighted by their notional. In comparison with other composite it should give an idea of which composite has more components needing to be rebalanced. */
  weightedAvgAbsDeviation(): number {
    if (this.portfolioComponents.length > 0) {

      const totalWeightedDeviation = this.portfolioComponents
        .map(component => Math.abs(component.deviation() * component.notional()))
        .reduce((acc, cur) => acc + cur, 0);

      const totalNotional = this.portfolioComponents
        .map(component => component.notional())
        .reduce((acc, cur) => acc + cur, 0);

      return totalWeightedDeviation / totalNotional;
    } else {
      return 0;
    }
  }

  /**
   * Sets the ideal notional value that this portfolio component should have.
   * @param totalPortfolioNotional 
   */
  setTotalPortfoliolNotional(totalPortfolioNotional: number): void {
    this.totalPortfolioNotional = totalPortfolioNotional;
    this.portfolioComponents.forEach(component => {
      component.setTotalPortfoliolNotional(totalPortfolioNotional);
    })
  }

  /**
   * Sets the ideal notional value that this portfolio component should have.
   * @param idealNotional 
   */
  setIdealNotional(idealNotional: number): void {
    this._idealNotional = idealNotional;
    const totalWeight = this.portfolioComponents.map(component => component.weight).reduce((accumulator, current) => accumulator + current, 0);
    this.portfolioComponents.forEach(component => {
      component.setIdealNotional(idealNotional * component.weight / totalWeight);
    })
  }

  /**
   * Sets the weight of positions with dynamic weight.
   */
  setDynamicWeight(): void {
    this.portfolioComponents.forEach(component => {
      component.setDynamicWeight();
    })
  }

  sort(): void {
    this.portfolioComponents.sort((a, b) => {
      // positions first
      if (a instanceof PortfolioPosition && b instanceof PortfolioComposite) {
        return -1
      }
      // then greater absolute deviation first
      return Math.abs(a.absoluteDeviation()) > Math.abs(b.absoluteDeviation()) ? -1 : a.absoluteDeviation() === b.absoluteDeviation() ? 0 : 1;
    });
    this.portfolioComponents.forEach(component => {
      if (component instanceof PortfolioComposite) {
        component.sort();
      } else if (component instanceof PortfolioPosition) {
        component.dynamicWeight?.weightPriceRanges.sort((a, b) => a.priceMax < b.priceMax ? -1 : 1);
      }
    })
  }

  clone(): PortfolioComponent {

    let portfolioComponentsClone = this.portfolioComponents.map(components => components.clone());

    let clone: PortfolioComposite =
      new PortfolioComposite(
        undefined,
        this.portfolioId,
        this.category,
        portfolioComponentsClone,
        this.weight);

    return clone;
  }

  /**
   * Removes a component from the portfolio.
   * @param componentId The id of the component to be removed.
   * @returns True if a component was removed, false otherwise.
   */
  removeComponent(componentId: string): boolean {
    let indexToRemove = undefined;
    let isPositionRemoved = false;
    this.portfolioComponents.forEach(component => {
      if ((component instanceof PortfolioComposite || component instanceof PortfolioPosition) && component.id === componentId) {
        indexToRemove = this.portfolioComponents.indexOf(component);
      } else if (component instanceof PortfolioComposite) {
        isPositionRemoved ||= component.removeComponent(componentId);
      }
    });
    if (indexToRemove !== undefined) {
      this.portfolioComponents.splice(indexToRemove, 1);
      return true;
    } else {
      return isPositionRemoved;
    }
  }

  /**
   * Add a component to the portfolio. Note that it will only be added if there is any composite whose id matches the component parent id.
   * @param componentId The component to be added.
   * @returns True if a component was added, false otherwise.
   */
  addComponent(componentToAdd: PortfolioComponent): boolean {
    let isComponentAdded = false;
    if (this.id === componentToAdd.parentCompositeId) {
      this.portfolioComponents.push(componentToAdd);
      return true;
    } else {
      this.portfolioComponents.forEach(component => {
        if (component instanceof PortfolioComposite) {
          isComponentAdded ||= component.addComponent(componentToAdd);
        }
      })
    }
    return isComponentAdded;
  }

  /**
   * Updates a composite in the portfolio.
   * @param composite The composite to be updated.
   * @returns True if a composite was updated, false otherwise.
   */
  updateComposite(compositeToUpdate: PortfolioComposite): boolean {
    let isComponentUpdated = false;
    if (this.id === compositeToUpdate.id) {
      this.weight = compositeToUpdate.weight;
      this.category.name = compositeToUpdate.category.name;
      this.category.description = compositeToUpdate.category.description;
      return true;
    }
    this.portfolioComponents.forEach(component => {
      if (component instanceof PortfolioComposite) {
        isComponentUpdated ||= component.updateComposite(compositeToUpdate);
      }
    });
    return isComponentUpdated;
  }

  updateComponent(componentToUpdate: PortfolioComponent): boolean {
    let isComponentUpdated = false;
    if ((componentToUpdate instanceof PortfolioComposite) && this.id === componentToUpdate.id) {
      Object.assign(this, componentToUpdate);
      return true;
    } else {
      this.portfolioComponents.forEach(component => {
        isComponentUpdated ||= component.updateComponent(componentToUpdate);
      });
    }
    return isComponentUpdated;
  }

  /**
   * Returns the requested component if it was found, returns undefined otherwise.
   * @param id The id to search.
   */
  getComponentById(id: string): PortfolioComponent | undefined {

    let result = undefined;

    if (this.id === id) {
      return this;
    }

    this.portfolioComponents.forEach(component => {
      if (component instanceof PortfolioComposite) {
        const childComponent = component.getComponentById(id);
        if (childComponent !== undefined) {
          result = childComponent;
        }
      } else if (component instanceof Position && component.id === id) {
        result = component
      }
    })

    return result;
  }

  /** Get all positions that belongs to this composite and any composite below it, recursively. */
  getAllPositions(): Position[] {
    const positions: Position[] = [];

    this.portfolioComponents.forEach(component => {
      if (component instanceof PortfolioComposite) {
        positions.push(...component.getAllPositions());
      } else if (component instanceof Position) {
        positions.push(component);
      }
    })

    return positions;
  }

  /**
   * Return the full category names path for a component.
   * @param componentId 
   * @returns The requested category names, as a sorted array of string, from the most far to the closest category. If the coponent was not found, return an empty array.
   */
  getCategoryPath(componentId: string): string[] {
    const result: string[] = [this.category.name];
    let componentFound: boolean = false;

    if (this.id === componentId) {
      return result;
    }

    this.portfolioComponents.forEach(component => {
      if (!componentFound) {
        if (component instanceof PortfolioComposite) {
          const childComponentPath = component.getCategoryPath(componentId);
          if (childComponentPath.length > 0) {
            result.push(...childComponentPath);
            componentFound = true;
          }
        } else if (component instanceof Position && component.id === componentId) {
          componentFound = true;
        }
      }
    })

    if (componentFound) {
      return result;
    } else {
      return [];
    }

  }

  /**
   * Returns the necessary items to rebalance all the nested positions under this composite.
   * @param newInvestment The notional value of new money to be invested, defaults to 0.
   * @param allowFractionQuantity Informs if fractionary quantities are allowed. e.g. brazilian stocks in generall allow 100 quantity buy/sell, but fractionary allows for 1.
   */
  calculateRebalanceItems(newInvestment: number = 0, allowFractionQuantity: boolean): RebalanceItem[] {
    const items: RebalanceItem[] = [];

    this.portfolioComponents.forEach(component => {
      if (component instanceof PortfolioComposite) {
        items.push(...component.calculateRebalanceItems(newInvestment, allowFractionQuantity));
      } else if (component instanceof Position) {
        items.push(component.calculateRebalanceItem(newInvestment, allowFractionQuantity));
      }
    });

    return items;
  }

}